import {
    Body_12_Coral_Primary_Regular12,
    Body_16_Coral_Primary_Regular_16,
    Title_16_Coral_Strong_emphasis_16,
    Title_20_Coral_Strong_emphasis_20
} from "@UI/typography/generated/typography.tsx";
import {Gt, GtDatetime, LocalDTFormater} from "@src/i18n/Gt.tsx";
import {Stack} from "@src/CommonComponents/Stack/Stack.tsx";
import {useQuery} from "@tanstack/react-query";
import {apiClient} from "@src/httpClient/api.ts";
import {KyuyoKubunEnum, StatementDetail} from "@src/httpClient/__generated__/Api.ts";
import {formatAmount, formatDuration} from "@src/utils/statementFormat.ts";
import {useParams} from "react-router";
import {useTranslation} from "react-i18next";
import {TFunction} from "i18next";
import cn from "classnames";
import styles from "./statementView.module.less";


// One printed cell: label above value. null keeps a printed gap in the row.
type Cell = { label: string, value: string, total?: boolean } | null

// A framed group of the slip; rows share the box's column count like the printout.
interface Box {
    caption?: string
    columns: number
    rows: Cell[][]
}

const attendanceBox = (t: TFunction, s: StatementDetail): Box => {
    const w = s.worktime_slip
    // Slips of hourly-paid employees print hours and an hourly rate in these cells.
    const hourly = s.kyuyo_kubun === KyuyoKubunEnum.Jikyu
    return {
        columns: 9,
        rows: [
            [
                hourly
                    ? {label: t('Shukkin jikan'), value: formatDuration(w?.shukkin_jikan)}
                    : {label: t('Shukkin nissu'), value: formatAmount(w?.shukkin_nissu)},
                hourly
                    ? {label: t('Kekkin jikan'), value: formatDuration(w?.kekkin_jikan)}
                    : {label: t('Kekkin nissu'), value: formatAmount(w?.kekkin_nissu)},
                {label: t('Chikoku kaisu'), value: formatAmount(w?.chikoku_kaisu)},
                {label: t('Sotai kaisu'), value: formatAmount(w?.sotai_kaisu)},
                {label: t('Jikangai jikan'), value: formatDuration(w?.jikangai_jikan)},
                {label: t('Shinya jikan'), value: formatDuration(w?.shinya_jikan)},
                {label: t('Kyujitsu jikan'), value: formatDuration(w?.kyujitsu_jikan)},
                {label: t('Kyujitsu shinya jikan'), value: formatDuration(w?.kyujitsu_shinya_jikan)},
                {label: t('Sonota jikan'), value: formatDuration(w?.sonota_jikan)}
            ],
            [
                hourly
                    ? {label: t('Jikyu tanka'), value: formatAmount(s.jikyu_tanka)}
                    : {label: t('Nikkyu tanka'), value: formatAmount(s.nikkyu_tanka)},
                {label: t('Kekkin tanka'), value: formatAmount(s.kekkin_tanka)},
                {label: t('Chikoku tanka'), value: formatAmount(s.chikoku_tanka)},
                {label: t('Sotai tanka'), value: formatAmount(s.sotai_tanka)},
                {label: t('Jikangai tanka'), value: formatAmount(s.jikangai_tanka)},
                {label: t('Shinya tanka'), value: formatAmount(s.shinya_tanka)},
                {label: t('Kyujitsu tanka'), value: formatAmount(s.kyujitsu_tanka)},
                {label: t('Kyujitsu shinya tanka'), value: formatAmount(s.kyujitsu_shinya_tanka)},
                {label: t('Sonota jikan tanka'), value: formatAmount(s.sonota_jikan_tanka)}
            ],
            [
                {label: t('Nisshuku kaisu'), value: formatAmount(w?.nisshuku_kaisu)},
                null,
                {label: t('Nisshuku tanka'), value: formatAmount(s.nisshuku_tanka)},
                null,
                {label: t('Zengetsu yukyu'), value: formatAmount(w?.zengetsu_yukyu)},
                null,
                {label: t('Togetsu yukyu'), value: formatAmount(w?.togetsu_yukyu)},
                null,
                {label: t('Yukyu zan'), value: formatAmount(w?.yukyu_zan)}
            ]
        ]
    }
}

const employeeBox = (t: TFunction, s: StatementDetail): Box => ({
    columns: 3,
    rows: [[
        {label: t('Code'), value: s.employee.employee_code == null ? '' : String(s.employee.employee_code)},
        {label: t('Name kana'), value: `${s.employee.last_name_kana} ${s.employee.first_name_kana}`.trim()},
        {label: t('Name'), value: `${s.employee.last_name} ${s.employee.first_name}`.trim()}
    ]]
})

const attributesBox = (t: TFunction, s: StatementDetail): Box => ({
    columns: 8,
    rows: [[
        {label: t('Seibetsu'), value: t(`GenderEnum.${s.employee.gender}`)},
        {label: t('Honnin kubun'), value: s.honnin_kubun ?? ''},
        {label: t('Haigusha'), value: s.haigusha ?? ''},
        {label: t('Fuyoshasu'), value: s.fuyoshasu ?? ''},
        {label: t('Shogaisha'), value: s.shogaisha ?? ''},
        {label: t('Zeigakuhyo'), value: s.zeigakuhyo ?? ''},
        {label: t('Kyuyo kubun'), value: s.kyuyo_kubun ? t(`KyuyoKubunEnum.${s.kyuyo_kubun}`) : ''},
        s.kyuyo_kubun === KyuyoKubunEnum.Jikyu
            ? {label: t('Rodo jikan'), value: formatDuration(s.rodo_jikan)}
            : {label: t('Rodo nissu'), value: s.rodo_nissu ?? ''}
    ]]
})

const paymentsBox = (t: TFunction, s: StatementDetail): Box => {
    const p = s.payment_slip
    return {
        caption: t('Shikyu'),
        columns: 7,
        rows: [
            [
                {label: t('Honkyu'), value: formatAmount(p?.honkyu)},
                {label: t('Yakuin hoshu'), value: formatAmount(p?.yakuin_hoshu)},
                {label: t('Yakushoku teate'), value: formatAmount(p?.yakushoku_teate)},
                {label: t('Kanrisha teate'), value: formatAmount(p?.kanrisha_teate)},
                {label: t('Zangyo teate'), value: formatAmount(p?.zangyo_teate)},
                {label: t('Sonota teate'), value: formatAmount(p?.sonota_teate)},
                {label: t('Hochodai'), value: formatAmount(p?.hochodai)}
            ],
            [
                null,
                {label: t('Shogatsu teate'), value: formatAmount(p?.shogatsu_teate)},
                {label: t('Jisshusei kanri teate'), value: formatAmount(p?.jisshusei_kanri_teate)},
                {label: t('Chusan seiseki teate'), value: formatAmount(p?.chusan_seiseki_teate)},
                {label: t('Teate 9'), value: formatAmount(p?.teate_9)},
                {label: t('Teate 10'), value: formatAmount(p?.teate_10)},
                null
            ],
            [
                null,
                {label: t('Jikangai teate'), value: formatAmount(p?.jikangai_teate)},
                {label: t('Shinya teate'), value: formatAmount(p?.shinya_teate)},
                {label: t('Kyujitsu teate'), value: formatAmount(p?.kyujitsu_teate)},
                {label: t('Kyujitsu shinya teate'), value: formatAmount(p?.kyujitsu_shinya_teate)},
                {label: t('Sonota jikan teate'), value: formatAmount(p?.sonota_jikan_teate)},
                {label: t('Nisshuku teate'), value: formatAmount(p?.nisshuku_teate)}
            ],
            [
                {label: t('Tsukinhi hikazei'), value: formatAmount(p?.tsukinhi_hikazei)},
                {label: t('Tsukinhi kazei'), value: formatAmount(p?.tsukinhi_kazei)},
                {label: t('Kekkin kojo'), value: formatAmount(p?.kekkin_kojo)},
                {label: t('Chikoku kojo'), value: formatAmount(p?.chikoku_kojo)},
                {label: t('Sotai kojo'), value: formatAmount(p?.sotai_kojo)},
                null,
                {label: t('Soshikyugaku'), value: formatAmount(p?.soshikyugaku), total: true}
            ]
        ]
    }
}

const deductionsBox = (t: TFunction, s: StatementDetail): Box => {
    const p = s.payment_slip
    return {
        caption: t('Kojo'),
        columns: 7,
        rows: [
            [
                {label: t('Kenko hokenryo'), value: formatAmount(p?.kenko_hokenryo)},
                {label: t('Kaigo hokenryo'), value: formatAmount(p?.kaigo_hokenryo)},
                {label: t('Kosei nenkin hokenryo'), value: formatAmount(p?.kosei_nenkin_hokenryo)},
                {label: t('Koyo hokenryo'), value: formatAmount(p?.koyo_hokenryo)},
                {label: t('Kazei taishogaku'), value: formatAmount(p?.kazei_taishogaku), total: true},
                {label: t('Juminzei'), value: formatAmount(p?.juminzei)},
                {label: t('Shotokuzei'), value: formatAmount(p?.shotokuzei)}
            ],
            [
                {label: t('Kibarai teikidai'), value: formatAmount(p?.kibarai_teikidai)},
                {label: t('Sonota shakai hokenryo'), value: formatAmount(p?.sonota_shakai_hokenryo)},
                {label: t('Kashitsukekin hensai'), value: formatAmount(p?.kashitsukekin_hensai)},
                {label: t('Yachin futankin'), value: formatAmount(p?.yachin_futankin)},
                {label: t('Internet dai'), value: formatAmount(p?.internet_dai)},
                {label: t('Seikatsuhi'), value: formatAmount(p?.seikatsuhi)},
                {label: t('Kojo 6'), value: formatAmount(p?.kojo_6)}
            ],
            [
                {label: t('Kojo 7'), value: formatAmount(p?.kojo_7)},
                {label: t('Tatekaekin'), value: formatAmount(p?.tatekaekin)},
                {label: t('Sonota kojo'), value: formatAmount(p?.sonota_kojo)},
                {label: t('Nenmatsu choseikin'), value: formatAmount(p?.nenmatsu_choseikin)},
                null,
                null,
                null
            ],
            [
                null,
                null,
                {label: t('Kojo gokei'), value: formatAmount(p?.kojo_gokei), total: true},
                null,
                {label: t('Zengetsu tangaku'), value: formatAmount(p?.zengetsu_tangaku)},
                {label: t('Togetsu tangaku'), value: formatAmount(p?.togetsu_tangaku)},
                {label: t('Sashihiki shikyugaku'), value: formatAmount(p?.sashihiki_shikyugaku), total: true}
            ]
        ]
    }
}

const yearToDateBox = (t: TFunction, s: StatementDetail): Box => ({
    columns: 7,
    rows: [[
        {label: t('Koyoritsu'), value: formatAmount(s.koyoritsu)},
        {label: t('Taishokusha'), value: s.taishokusha ?? ''},
        {label: t('Hikazei'), value: s.hikazei == null ? '' : s.hikazei ? t('Hikazei ari') : t('Hikazei nashi')},
        {label: t('Kazei taishogaku ruikei'), value: formatAmount(s.kazei_taishogaku_ruikei)},
        {label: t('Kazei shikyu ruikei'), value: formatAmount(s.kazei_shikyu_ruikei)},
        {label: t('Shakai hoken ruikei'), value: formatAmount(s.shakai_hoken_ruikei)},
        {label: t('Shotokuzei ruikei'), value: formatAmount(s.shotokuzei_ruikei)}
    ]]
})

const SlipBox = ({box, className}: { box: Box, className?: string }) => (
    <div className={cn(styles.box, className)}>
        {box.caption && (
            <div className={styles.caption}>
                <Title_16_Coral_Strong_emphasis_16>{box.caption}</Title_16_Coral_Strong_emphasis_16>
            </div>
        )}
        <div className={styles.rows}>
            {box.rows.map((row, i) => (
                <div key={i} className={styles.row} style={{gridTemplateColumns: `repeat(${box.columns}, 1fr)`}}>
                    {row.map((cell, j) => (
                        <div key={j} className={cn(styles.cell, {[styles.total]: cell?.total})}>
                            {cell && (
                                <>
                                    <div className={styles.label}>
                                        <Body_12_Coral_Primary_Regular12>{cell.label}</Body_12_Coral_Primary_Regular12>
                                    </div>
                                    <div className={styles.value}>
                                        <Body_16_Coral_Primary_Regular_16>{cell.value}</Body_16_Coral_Primary_Regular_16>
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    </div>
)

export const StatementView = () => {
    const {statementId} = useParams<{ statementId: string }>()
    const {t} = useTranslation()

    const {data: statement, isLoading} = useQuery({
        queryKey: ['statement', statementId],
        queryFn: async () => (await apiClient.api.accountingStatementsRetrieve(Number(statementId))).data,
        enabled: !!statementId
    })

    if (!statement) {
        return (
            <Stack direction={"column"} style={{padding: 16}}>
                {isLoading ? <><Gt>Loading</Gt>...</> : <Gt>No data</Gt>}
            </Stack>
        )
    }

    return (
        <div className={styles.root}>
            {/* The slip keeps its printed width; narrow screens scroll it sideways. */}
            <div className={styles.sheet}>
                <div className={styles.line}>
                    <Stack direction={"column"} gap={8} className={styles.heading}>
                        <Title_20_Coral_Strong_emphasis_20><Gt>Pay slip</Gt></Title_20_Coral_Strong_emphasis_20>
                        <Body_16_Coral_Primary_Regular_16>
                            <GtDatetime template={LocalDTFormater.LocalMonth}>
                                {`${statement.period_year}-${String(statement.period_month).padStart(2, '0')}-01`}
                            </GtDatetime>
                        </Body_16_Coral_Primary_Regular_16>
                        {statement.payment_date && (
                            <Body_12_Coral_Primary_Regular12>
                                <Gt>Payment date</Gt>{' '}
                                <GtDatetime template={LocalDTFormater.LocalDate}>{statement.payment_date}</GtDatetime>
                            </Body_12_Coral_Primary_Regular12>
                        )}
                    </Stack>
                    <SlipBox box={attendanceBox(t, statement)} className={styles.wide}/>
                </div>
                <div className={styles.line}>
                    <SlipBox box={employeeBox(t, statement)} className={styles.narrow}/>
                    <SlipBox box={attributesBox(t, statement)} className={styles.wide}/>
                </div>
                <SlipBox box={paymentsBox(t, statement)}/>
                <SlipBox box={deductionsBox(t, statement)}/>
                <SlipBox box={yearToDateBox(t, statement)}/>
            </div>
        </div>
    )
}
